import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAnnualRevenueReport } from "../../../state/reducers/totalRevenueSlice";
import { Bar, Line } from "react-chartjs-2";
import "chart.js/auto";

const TotalRevenueReport = () => {
  const dispatch = useDispatch();
  const { data, status, error } = useSelector(
    (state) => state.totalRevenueReport
  ); // Updated the state name
  const [groupBy, setGroupBy] = useState("month"); // Default grouping: month
  const [reportType, setReportType] = useState("Analytics"); // Default report type: Analytics
  const [generateClicked, setGenerateClicked] = useState(false);
  useEffect(() => {
    dispatch(fetchAnnualRevenueReport(groupBy)); // Fetch data for the selected group (month/year)
  }, [dispatch, groupBy]);

  // Prepare chart data based on the fetched data
  const getChartData = () => {
    if (status === "succeeded" && data) {
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      let labels = months;
      let revenues = new Array(12).fill(0);
      if (groupBy === "month" && data.monthly_revenue) {
        // Iterate through monthly_revenue and extract month and total_revenue
        data.monthly_revenue.forEach((record) => {
          record.monthly_revenue.forEach((monthData) => {
            const monthIndex = months.indexOf(monthData.month);
            if (monthIndex !== -1) {
              revenues[monthIndex] = monthData.total_revenue;
            }
          });
        });

        return {
          labels,
          datasets: [
            {
              label: `Monthly Revenue`,
              data: revenues,
              backgroundColor: "#1e40af", // Dark Blue color
              borderColor: "#00008B",
              borderWidth: 1,
            },
          ],
        };
      }

      if (groupBy === "year" && data.yearly_revenue) {
        const labels = data.yearly_revenue.map((item) => item.year);
        const revenues = data.yearly_revenue.map((item) => item.total_revenue);

        return {
          labels,
          datasets: [
            {
              label: `Yearly Revenue`,
              data: revenues,
              backgroundColor: "#1e40af", // Dark Blue color
              borderColor: "#00008B",
              borderWidth: 1,
            },
          ],
        };
      }
    }

    return {
      labels: [],
      datasets: [
        {
          label: "No Data Available",
          data: [],
        },
      ],
    };
  };
  const handleGenerate = () => {
    setGenerateClicked(true); // Set flag to true when Generate is clicked
  };

  // const renderList = () => {
  //     if (generateClicked && status === "succeeded" && data) {
  //       // Conditionally choose the correct revenue data based on `groupBy`
  //       const revenueData =
  //         groupBy === "month" && data.monthly_revenue
  //           ? data.monthly_revenue
  //           : groupBy === "year" && data.yearly_revenue
  //           ? data.yearly_revenue
  //           : [];

  //       if (revenueData.length === 0) {
  //         return <div className="text-center text-gray-500">No data available</div>;
  //       }

  //       return (
  //         <div className="bg-white p-6 rounded-lg shadow-lg">
  //           <h3 className="text-xl font-semibold mb-4">Revenue Data</h3>
  //           <table className="min-w-full">
  //             <thead>
  //               <tr>
  //                 <th className="px-4 py-2 text-left">Month/Year</th>
  //                 <th className="px-4 py-2 text-left">Revenue</th>
  //               </tr>
  //             </thead>
  //             <tbody>
  //               {revenueData.map((item, index) => (
  //                 <tr key={index}>
  //                   <td className="px-4 py-2">{item.month || item.year}</td>
  //                   <td className="px-4 py-2">{item.total_revenue}</td>
  //                 </tr>
  //               ))}
  //             </tbody>
  //           </table>
  //         </div>
  //       );
  //     }

  //     return <div className="text-center text-gray-500">No data available</div>;
  //   };

  const renderList = () => {
    if (generateClicked && status === "succeeded" && data) {
      // Conditionally choose the correct revenue data based on `groupBy`
      let revenueData = [];

      if (groupBy === "month" && data.monthly_revenue) {
        // Ensure monthly_revenue exists and is in the correct format
        revenueData = data.monthly_revenue.flatMap((yearData) =>
          yearData.monthly_revenue.map((monthData) => ({
            month: monthData.month,
            total_revenue: monthData.total_revenue,
          }))
        );
      } else if (groupBy === "year" && data.yearly_revenue) {
        // Ensure yearly_revenue exists and is in the correct format
        revenueData = data.yearly_revenue.map((yearData) => ({
          year: yearData.year,
          total_revenue: yearData.total_revenue,
        }));
      }

      // If no data is available
      if (revenueData.length === 0) {
        return (
          <div className="text-center text-gray-500">No data available</div>
        );
      }

      return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h3 className="text-xl font-semibold mb-4">Revenue Data</h3>
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left">Month/Year</th>
                <th className="px-4 py-2 text-left">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {revenueData.map((item, index) => (
                <tr key={index}>
                  <td className="px-4 py-2">{item.month || item.year}</td>
                  <td className="px-4 py-2">{item.total_revenue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return <div className="text-center text-gray-500">No data available</div>;
  };

  const chartData = getChartData();

  return (
    <div className="bg-white p-6 rounded-md shadow-md">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-bold">Revenue Report</h1>
        <div className="flex gap-4">
          <button
            onClick={() => setGroupBy("month")}
            className={`px-4 py-2 rounded-md ${
              groupBy === "month"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setGroupBy("year")}
            className={`px-4 py-2 rounded-md ${
              groupBy === "year"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            Years
          </button>
        </div>
      </div>

      <div className="flex gap-4">
        <div className="flex gap-4">
          <select
            className="px-4 py-2 rounded-md"
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
          >
            <option value="Analytics">Analytics</option>
            <option value="List">List</option>
          </select>
        </div>

        <div className="">
          <button
            onClick={handleGenerate}
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            Generate
          </button>
        </div>
      </div>

      {status === "loading" && <p className="text-gray-500">Loading...</p>}
      {status === "failed" && <p className="text-red-500">{error}</p>}

      {reportType === "Analytics" ? (
        <Bar
          data={chartData}
          options={{
            responsive: true,
            plugins: {
              legend: { display: true },
            },
            scales: {
              y: {
                beginAtZero: true,
              },
            },
          }}
        />
      ) : (
        renderList() // Show table when report type is 'List'
      )}

      {/* {chartData && chartData.labels.length > 0 && (
        <div>
          {groupBy === "month" ? (
            <Bar
              data={chartData}
              options={{
                responsive: true,
                plugins: {
                  legend: { display: true },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                  },
                },
              }}
            />
          ) : (
            <Bar
              data={chartData}
              options={{
                responsive: true,
                plugins: {
                  legend: { display: true },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                  },
                },
              }}
            />
          )}
        </div>
      )} */}
    </div>
  );
};

export default TotalRevenueReport;
